import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { ToastEvent, ToastKind } from '../lib/toast'

/**
 * Renders transient toasts fired via `toast()` from lib/toast. A single
 * instance mounts in App. Toasts stack bottom-center, auto-dismiss after
 * ~3.2s, and animate in with a quick lift (matches the app's 200ms ease
 * language). pointer-events pass through except on the pills themselves.
 */
export function Toaster() {
  const [toasts, setToasts] = useState<ToastEvent[]>([])

  useEffect(() => {
    function onToast(e: Event) {
      const t = (e as CustomEvent<ToastEvent>).detail
      setToasts((cur) => [...cur, t])
      window.setTimeout(() => {
        setToasts((cur) => cur.filter((x) => x.id !== t.id))
      }, 3200)
    }
    window.addEventListener('aska:toast', onToast as EventListener)
    return () => window.removeEventListener('aska:toast', onToast as EventListener)
  }, [])

  return (
    <div
      className="fixed bottom-5 left-1/2 z-[80] flex -translate-x-1/2 flex-col items-center gap-2 pointer-events-none"
      aria-live="polite"
      role="status"
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto inline-flex h-11 items-center gap-2 rounded-full bg-navy px-4 text-sm font-medium text-white shadow-lift"
          >
            <ToastIcon kind={t.kind} />
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}

function ToastIcon({ kind }: { kind: ToastKind }) {
  const color =
    kind === 'success' ? 'text-success' : kind === 'error' ? 'text-danger' : 'text-white/70'
  return (
    <span className={`inline-flex h-5 w-5 items-center justify-center ${color}`}>
      {kind === 'success' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : kind === 'error' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="8" /><line x1="12" y1="12" x2="12" y2="16" />
        </svg>
      )}
    </span>
  )
}
